import { LineGame, TraceResult, parseSegmentKey } from './LineGame.js';
import { Grid } from '../../core/Grid.js';
import { Point, Direction, pointKey, pointsEqual } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface LineRenderConfig extends Partial<RenderConfig> {
    lineColor?: string;
    lineWidth?: number;
    targetColor?: string;
    targetHitColor?: string;
    targetRadius?: number;
    diamondSize?: number;
}

const LINE_DEFAULTS = {
    lineColor: COLORS.teal,
    disconnectedColor: COLORS.amber,
    lineWidth: 3,
    targetRadius: 8,
    diamondSize: 12,
};

// ── Segment and pin animation (docs/juice.md) ─────────────────────────
//
// A segment is drawn on, not popped on — and it grows *out of the line the
// player is already drawing*. Whichever of its two ends already carries other
// segments is the end it grows from; with both ends occupied (closing a gap) or
// neither (a stray segment out on its own) there is nothing to grow out of, so
// it opens from its midpoint instead. Removing runs the same thing backwards,
// judged against the board that remains, so a segment always retracts toward
// what is left of the line.

type SegOrigin = 'a' | 'b' | 'mid';

interface SegTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    origin: SegOrigin;
    /** Exit only: how it was drawn on the last frame it existed. */
    color: string;
    dashed: boolean;
}

export class LineRenderer extends GridRenderer {
    private lineConfig: typeof LINE_DEFAULTS;
    private gateMode: boolean = false;
    private hintsEnabled: boolean = true;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private segTweens = new Map<string, SegTween>();
    private segState = new Set<string>();                          // what the last frame drew
    private segStyles = new Map<string, { color: string; dashed: boolean }>();
    private segsSynced: boolean = false;                           // first frame seeds
    private pinPoint: Point | null = null;                         // where the pin is now
    private pinEnterStart: number | null = null;                   // it growing in there
    private pinExit: { point: Point; start: number } | null = null; // the one it left behind
    // Targets landing. A corner turning from ring to dot is the game answering, but
    // it is also the one outcome the whole game is about, so it gets punctuated —
    // `emphasis`, whose curve is past full size early, so nothing is withheld.
    private hitCorners = new Set<string>();                        // what the last frame drew
    private hitPops = new Map<string, number>();                   // cornerKey -> tween start
    private hitsSynced: boolean = false;                           // first frame seeds
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: LineGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: LineRenderConfig = {}) {
        super(canvas, grid, config);
        this.lineConfig = { ...LINE_DEFAULTS, ...config };
        // The line's digits are targets at the corners, so the fine cursor draws its
        // intersections as a wide-gap crosshair (clearing the target) rather than a dot.
        this.keyboardFinePointCursor = true;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every segment and the pin at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.segTweens.clear();
        this.pinEnterStart = null;
        this.pinExit = null;
        this.hitPops.clear();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Toggle the teal/amber chevron hints (on by default). */
    setHintsEnabled(enabled: boolean): void {
        this.hintsEnabled = enabled;
    }

    /** Whether chevron hints are shown — gates the click-to-trace input convenience. */
    getHintsEnabled(): boolean {
        return this.hintsEnabled;
    }

    render(game: LineGame): void {
        const grid = game.getGrid();
        const result = game.trace();

        this.lastGame = game;
        this.animating = false;
        this.syncPinTween(game, this.tweens.now());
        this.syncHitPops(result.hitCorners);

        this.clear();
        this.drawGrid();

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawSegments(game, result);
        this.drawGateJumps(result);
        this.drawRimIndicators(game, result);
        this.drawRimJumpHint(result);
        this.drawDigitTargets(game, result);
        this.drawGhostStart(game);
        this.drawStartPoint(game);
        this.drawLastTargetDiamonds(game, result);
        this.drawHints(game, result);
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
        }
        this.drawDigits(grid);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    private getGridPointPixel(p: Point): { x: number; y: number } {
        const { padding, cellSize } = this.config;
        return {
            x: padding + p.col * cellSize,
            y: padding + p.row * cellSize
        };
    }

    /**
     * Note the corners the line has just reached, so they can be popped. Only newly
     * hit ones: a corner going *back* to unhit simply reverts, since losing a target
     * isn't an outcome to celebrate and the ring underneath says it plainly.
     */
    private syncHitPops(hit: Set<string>): void {
        if (!this.tweens.isEnabled()) {
            this.hitPops.clear();
            this.hitCorners = new Set(hit);
            return;
        }
        // First frame on this board: adopt it, or opening a solved grid would set
        // every target off at once.
        if (!this.hitsSynced) {
            this.hitCorners = new Set(hit);
            this.hitsSynced = true;
            return;
        }

        const now = this.tweens.now();
        for (const key of hit) {
            if (!this.hitCorners.has(key)) this.hitPops.set(key, now);
        }
        for (const key of this.hitCorners) {
            if (!hit.has(key)) this.hitPops.delete(key);
        }
        this.hitCorners = new Set(hit);
    }

    /** How big a just-hit target reads this frame — 1 unless it is still landing. */
    private hitPopScale(cornerKey: string, now: number): number {
        const start = this.hitPops.get(cornerKey);
        if (start === undefined) return 1;
        const { value, done } = this.tweens.tween(start, MOTION.emphasis, now);
        if (done) {
            this.hitPops.delete(cornerKey);
            return 1;
        }
        this.animating = true;
        return value;
    }

    private drawDigitTargets(game: LineGame, result: TraceResult): void {
        const clearedSet = new Set(result.clearedDigits);
        const nextDigit = typeof result.nextTarget === 'number' ? result.nextTarget : null;

        const diamondCorners = this.getDiamondCorners(game, result);
        const now = this.tweens.now();

        for (const [digit, target] of game.getDigitTargets()) {
            const isCleared = clearedSet.has(digit);
            const isNextDigit = digit === nextDigit;
            // Teal dots for the next/cleared digit, amber for future digits.
            const inOrder = isCleared || isNextDigit;
            const color = inOrder ? COLORS.teal : COLORS.amber;

            const hitOrder = this.computeDigitHitOrder(target.corners, result.path);
            const total = target.corners.length;

            for (const corner of target.corners) {
                const cornerKey = pointKey(corner);
                if (diamondCorners.has(cornerKey)) continue;

                const pixel = this.getGridPointPixel(corner);
                const isHit = result.hitCorners.has(cornerKey);
                const radius = this.lineConfig.targetRadius;

                if (isHit) {
                    // The dot lands with a swing past full size, pips and all — they
                    // are part of the same mark. The ring stays under it at rest size
                    // while it grows, so the target never appears to move or vanish.
                    const scale = this.hitPopScale(cornerKey, now);
                    if (scale < 1) {
                        this.ctx.beginPath();
                        this.ctx.arc(pixel.x, pixel.y, radius, 0, Math.PI * 2);
                        this.ctx.strokeStyle = color;
                        this.ctx.lineWidth = 2;
                        this.ctx.stroke();
                    }

                    this.ctx.save();
                    this.ctx.translate(pixel.x, pixel.y);
                    this.ctx.scale(scale, scale);

                    this.ctx.beginPath();
                    this.ctx.arc(0, 0, radius, 0, Math.PI * 2);
                    this.ctx.fillStyle = color;
                    this.ctx.fill();

                    // Overlay small pips showing the order this target was hit:
                    // 1st hit → one pip, 2nd → two, 3rd → three. A two-target
                    // (edge) digit's single non-end target shows two pips.
                    const ordinal = hitOrder.get(cornerKey);
                    if (ordinal) {
                        const count = total === 2 ? 2 : ordinal;
                        this.drawPips(0, 0, radius, count, this.config.backgroundColor);
                    }
                    this.ctx.restore();
                } else {
                    this.ctx.beginPath();
                    this.ctx.arc(pixel.x, pixel.y, radius, 0, Math.PI * 2);
                    this.ctx.strokeStyle = color;
                    this.ctx.lineWidth = 2;
                    this.ctx.stroke();
                }
            }
        }
    }

    /**
     * Order in which each of a digit's target corners was first reached along the
     * traced path. Returns cornerKey → 1-based ordinal (only for reached corners).
     */
    private computeDigitHitOrder(corners: Point[], path: Point[]): Map<string, number> {
        const cornerKeys = new Set(corners.map(c => pointKey(c)));
        const order = new Map<string, number>();
        let ordinal = 0;
        for (const p of path) {
            const pk = pointKey(p);
            if (cornerKeys.has(pk) && !order.has(pk)) {
                order.set(pk, ++ordinal);
            }
        }
        return order;
    }

    /**
     * Draw `count` small pips (1-4) inside a dot or diamond to show hit order, like
     * dice. `offset` is how far each pip sits from the centre; it defaults to a
     * dot's spacing, and callers drawing into a larger shape can push it out.
     */
    private drawPips(
        x: number, y: number, r: number, count: number, color: string,
        offset: number = r * 0.42
    ): void {
        const pipR = r * 0.24;
        const o = offset;
        const centers: Array<[number, number]> =
            count <= 1 ? [[0, 0]] :
            count === 2 ? [[-o, 0], [o, 0]] :
            count === 3 ? [[0, -o], [-o * 0.87, o * 0.5], [o * 0.87, o * 0.5]] :  // triangle
            [[0, -o], [o, 0], [0, o], [-o, 0]];  // diamond, echoing the shape it sits in

        this.ctx.fillStyle = color;
        for (const [dx, dy] of centers) {
            this.ctx.beginPath();
            this.ctx.arc(x + dx, y + dy, pipR, 0, Math.PI * 2);
            this.ctx.fill();
        }
    }

    /**
     * The end corner of each completed digit — the last of its targets the line
     * reached — drawn as a diamond rather than a dot. Teal when the digit was
     * cleared in its turn, red when it was completed too early.
     */
    private getEndCorners(
        game: LineGame, result: TraceResult
    ): Array<{ digit: number; point: Point; color: string }> {
        const ends: Array<{ digit: number; point: Point; color: string }> = [];

        for (const [digit, point] of result.lastTargetPerDigit) {
            ends.push({ digit, point, color: COLORS.teal });
        }

        for (const digit of result.outOfOrderDigits) {
            const target = game.getDigitTargets().get(digit);
            if (!target) continue;

            let lastCorner: Point | null = null;
            for (const point of result.path) {
                for (const corner of target.corners) {
                    if (corner.row === point.row && corner.col === point.col) {
                        lastCorner = corner;
                    }
                }
            }

            if (lastCorner) ends.push({ digit, point: lastCorner, color: COLORS.red });
        }

        return ends;
    }

    private getDiamondCorners(game: LineGame, result: TraceResult): Set<string> {
        return new Set(this.getEndCorners(game, result).map(e => pointKey(e.point)));
    }

    private drawSegments(game: LineGame, result: TraceResult): void {
        const segments = game.getSegments();
        this.syncSegmentTweens(segments);
        const now = this.tweens.now();

        this.ctx.lineWidth = this.lineConfig.lineWidth;
        this.ctx.lineCap = 'round';

        for (const segKey of segments) {
            // Color priority: red for violations, solid teal for traced, dashed teal for mirrored, amber for disconnected
            const isViolation = result.violationKeys.has(segKey);
            const isTraced = result.tracedSegmentKeys.has(segKey);
            const isMirrored = result.mirroredSegmentKeys.has(segKey);

            const style = isViolation ? { color: COLORS.red, dashed: false }
                : isTraced ? { color: this.lineConfig.lineColor, dashed: false }
                : isMirrored ? { color: this.lineConfig.lineColor, dashed: true }
                : { color: this.lineConfig.disconnectedColor, dashed: false };
            this.segStyles.set(segKey, style);

            const tween = this.segTweens.get(segKey);
            const extent = tween ? this.segExtent(tween, now) : { origin: 'mid' as SegOrigin, grown: 1, done: true };
            if (tween && extent.done) this.segTweens.delete(segKey);
            else if (tween) this.animating = true;

            this.drawSegmentStroke(segKey, extent.origin, extent.grown, style);
        }

        // Segments being removed are already gone from game state — they only
        // exist here, retracting in the colours they wore when they went.
        for (const [segKey, tween] of this.segTweens) {
            if (tween.kind !== 'exit' || segments.has(segKey)) continue;
            const extent = this.segExtent(tween, now);
            if (extent.done) {
                this.segTweens.delete(segKey);
                this.segStyles.delete(segKey);
                continue;
            }
            this.animating = true;
            this.drawSegmentStroke(segKey, extent.origin, extent.grown,
                { color: tween.color, dashed: tween.dashed });
        }

        this.ctx.setLineDash([]);
    }

    /** Draw the `grown` fraction of a segment, extending from `origin`. */
    private drawSegmentStroke(
        segKey: string, origin: SegOrigin, grown: number,
        style: { color: string; dashed: boolean }
    ): void {
        if (grown <= 0) return;
        const [from, to] = parseSegmentKey(segKey);
        const a = this.getGridPointPixel(from);
        const b = this.getGridPointPixel(to);

        // Fractions along a→b that the drawn part spans.
        const [t0, t1] = origin === 'a' ? [0, grown]
            : origin === 'b' ? [1 - grown, 1]
            : [0.5 - grown / 2, 0.5 + grown / 2];

        this.ctx.strokeStyle = style.color;
        this.ctx.setLineDash(style.dashed ? [4, 4] : []);
        this.ctx.beginPath();
        this.ctx.moveTo(a.x + (b.x - a.x) * t0, a.y + (b.y - a.y) * t0);
        this.ctx.lineTo(a.x + (b.x - a.x) * t1, a.y + (b.y - a.y) * t1);
        this.ctx.stroke();
    }

    /**
     * Diff the board against the last frame and start a tween per changed
     * segment. A bulk change (Reset, Restore best) fans out in reading order so a
     * restored line draws itself on rather than appearing whole.
     */
    private syncSegmentTweens(segments: Set<string>): void {
        if (!this.tweens.isEnabled()) {
            this.segTweens.clear();
            this.segState = new Set(segments);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a
        // saved solution would redraw the whole line.
        if (!this.segsSynced) {
            this.segState = new Set(segments);
            this.segsSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of segments) if (!this.segState.has(key)) added.push(key);
        for (const key of this.segState) if (!segments.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) return;

        const rank = (key: string) => {
            const [p] = parseSegmentKey(key);
            return p.row * (this.grid.size + 1) + p.col;
        };
        const changed = [...added, ...removed].sort((x, y) => rank(x) - rank(y));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (segments.has(key)) {
                // Grow out of the line as it stood *before* this segment joined it.
                this.segTweens.set(key, {
                    kind: 'enter', start, origin: this.segOrigin(key, this.segState, true),
                    color: '', dashed: false
                });
            } else {
                const style = this.segStyles.get(key);
                // Retract toward whatever is left of the line now it has gone.
                this.segTweens.set(key, {
                    kind: 'exit', start, origin: this.segOrigin(key, segments, false),
                    color: style?.color ?? this.lineConfig.disconnectedColor,
                    dashed: style?.dashed ?? false
                });
            }
        });

        this.segState = new Set(segments);
    }

    /**
     * Which end a segment grows from.
     *
     * A keyboard trace hop lands the cursor on the intersection it reached, so that
     * end is where the gesture arrived: a new segment grows *away* from it and a
     * removed one retracts *into* it. Asked first, because connectivity alone gets
     * it backwards whenever you trace *toward* line that is already there — which is
     * exactly when you are joining a run up.
     *
     * Off a gesture it falls back to the board *without* this piece: the end that
     * already carries line, or the midpoint when both ends do (closing a gap) or
     * neither does (a segment out on its own).
     */
    private segOrigin(segKey: string, others: Set<string>, entering: boolean): SegOrigin {
        const ka = segKey.slice(0, 2);
        const kb = segKey.slice(2, 4);

        const cursor = this.keyboardIntersection();
        if (cursor) {
            const cursorKey = pointKey(cursor);
            const atA = cursorKey === ka;
            const atB = cursorKey === kb;
            if (atA !== atB) {
                if (entering) return atA ? 'b' : 'a';
                return atA ? 'a' : 'b';
            }
        }

        let atA = false, atB = false;
        for (const other of others) {
            if (other === segKey) continue;
            const oa = other.slice(0, 2), ob = other.slice(2, 4);
            if (oa === ka || ob === ka) atA = true;
            if (oa === kb || ob === kb) atB = true;
            if (atA && atB) break;
        }
        if (atA === atB) return 'mid';
        return atA ? 'a' : 'b';
    }

    /** How much of a segment is on the board this frame, and from which end. */
    private segExtent(
        tween: SegTween, now: number
    ): { origin: SegOrigin; grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it to get what's left.
        return { origin: tween.origin, grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    private drawRimIndicators(game: LineGame, result: TraceResult): void {
        const startPoint = game.getStartPoint();
        const triangleSize = 8;
        // The path's terminal rim point is the line's exit; draw its triangle
        // larger so it reads as the endpoint, distinct from mid-path wrap markers.
        const exitTriangleSize = 12;

        this.ctx.fillStyle = this.lineConfig.lineColor;

        // Gate jumps land on the rim too — the corner of a gate cell that touches it.
        // Such a landing is an entry just like a wrap's, so the arrow there must point
        // into the grid, not out of it.
        const gateJumpKeys = new Set(
            result.gateJumps.map(([from, to]) => pointKey(from) + '>' + pointKey(to))
        );

        for (let i = 0; i < result.path.length; i++) {
            const point = result.path[i];

            if (!this.grid.isRimPoint(point)) continue;
            if (this.grid.isCornerPoint(point)) continue;
            if (startPoint && point.row === startPoint.row && point.col === startPoint.col) continue;

            const prev = i > 0 ? result.path[i - 1] : null;
            const next = i < result.path.length - 1 ? result.path[i + 1] : null;
            const prevWrapped = prev && this.grid.getWrapPoint(prev);
            const pointWrapped = this.grid.getWrapPoint(point);
            const isEntry = prev && (
                (prevWrapped && pointsEqual(prevWrapped, point))
                || gateJumpKeys.has(pointKey(prev) + '>' + pointKey(point))
            );
            const isExit = next && pointWrapped && pointsEqual(pointWrapped, next);
            const pointOutward = !isEntry || isExit;

            let angle = 0;
            if (point.row === 0) angle = pointOutward ? 0 : Math.PI;
            else if (point.row === this.grid.size) angle = pointOutward ? Math.PI : 0;
            else if (point.col === 0) angle = pointOutward ? -Math.PI / 2 : Math.PI / 2;
            else if (point.col === this.grid.size) angle = pointOutward ? Math.PI / 2 : -Math.PI / 2;

            // Nudge the indicator inward off the rim so it stays distinct from the
            // start/entry marker, which sits on the rim itself.
            const inset = 2;
            let ox = 0, oy = 0;
            if (point.row === 0) oy = inset;
            else if (point.row === this.grid.size) oy = -inset;
            else if (point.col === 0) ox = inset;
            else if (point.col === this.grid.size) ox = -inset;

            const pixel = this.getGridPointPixel(point);
            // The exit marker (terminal rim point) is enlarged, but only on a win —
            // otherwise the last point is just where an unfinished path happens to stop.
            const isExitMarker = result.won && i === result.path.length - 1;
            const size = isExitMarker ? exitTriangleSize : triangleSize;

            this.ctx.save();
            this.ctx.translate(pixel.x + ox, pixel.y + oy);
            this.ctx.rotate(angle);
            this.ctx.beginPath();
            this.ctx.moveTo(0, -size);
            this.ctx.lineTo(-size, size * 0.5);
            this.ctx.lineTo(size, size * 0.5);
            this.ctx.closePath();
            this.ctx.fill();
            this.ctx.restore();
        }
    }

    /**
     * Newcomer hint chevrons (mirrors Branches' hint rings). Before any segment is
     * drawn, inward chevrons at the rim mark where the start can be (re)placed. Once
     * the line is started, chevrons at the traced path's tip point out each valid
     * next-segment direction — teal when the target point is free, amber when a
     * segment already meets there (extending would branch).
     */
    private drawHints(game: LineGame, result: TraceResult): void {
        if (!this.hintsEnabled || result.won) return;

        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash([]);

        // Before the line is drawn: fainter inward chevrons at the rim intersections
        // a start can move to (the current start keeps its pin).
        if (!game.isStarted()) {
            this.ctx.strokeStyle = colorWithAlpha('teal', 0.55);
            for (const p of game.getStartHintPoints()) {
                const { angle, ox, oy } = this.inwardChevron(p, 7);
                const pixel = this.getGridPointPixel(p);
                this.drawChevron(pixel.x + ox, pixel.y + oy, angle, 5);
            }
        }

        // Valid next-segment directions from the tip (the tip is the start point
        // itself when nothing is drawn yet, so this also hints the first move in
        // front of the start). Teal for a clean extension, amber if it would branch.
        for (const h of game.getSegmentHints()) {
            const a = this.getGridPointPixel(h.from);
            const b = this.getGridPointPixel(h.to);
            this.ctx.strokeStyle = colorWithAlpha(h.free ? 'teal' : 'amber', 0.7);
            this.drawChevron((a.x + b.x) / 2, (a.y + b.y) / 2, this.pointAngle(h.from, h.to), 6);
        }
    }

    /** Stroke a small chevron ("^") centred at (x,y), apex pointing along `angle`
     *  (0 = up/N, measured clockwise). */
    private drawChevron(x: number, y: number, angle: number, size: number): void {
        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        this.ctx.beginPath();
        this.ctx.moveTo(-size, size * 0.45);
        this.ctx.lineTo(0, -size * 0.55);
        this.ctx.lineTo(size, size * 0.45);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /** Clockwise chevron rotation so the apex points from `from` toward `to`. */
    private pointAngle(from: Point, to: Point): number {
        if (to.row < from.row) return 0;               // N (up)
        if (to.row > from.row) return Math.PI;         // S (down)
        if (to.col > from.col) return Math.PI / 2;     // E (right)
        return -Math.PI / 2;                           // W (left)
    }

    /** Chevron angle (apex pointing into the grid) and inward pixel nudge for a
     *  rim point, so the chevron sits just off the rim. */
    private inwardChevron(p: Point, d: number): { angle: number; ox: number; oy: number } {
        const dir: Direction = this.grid.rimInwardDirection(p) ?? 'S';
        const angle: Record<Direction, number> = { N: 0, E: Math.PI / 2, S: Math.PI, W: -Math.PI / 2 };
        const nudge: Record<Direction, [number, number]> = { N: [0, -d], E: [d, 0], S: [0, d], W: [-d, 0] };
        return { angle: angle[dir], ox: nudge[dir][0], oy: nudge[dir][1] };
    }

    /**
     * Gate jumps: solid purple arcs, bowed to the right of travel like Jumps' jump
     * arcs. A straight chord reads as a drawn segment when it lands on a grid line,
     * and a pair of jumps both ways between the same corners would overlap into one
     * stroke; the bow keeps both apart from the line and from each other.
     */
    private drawGateJumps(result: TraceResult): void {
        if (result.gateJumps.length === 0) return;

        const { cellSize } = this.config;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = this.lineConfig.lineWidth;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);

        for (const [from, to] of result.gateJumps) {
            const a = this.getGridPointPixel(from);
            const b = this.getGridPointPixel(to);

            // Control point: midpoint pushed right of travel (screen y grows down, so
            // rotating the heading clockwise is (x, y) -> (-y, x)). Proportional to the
            // jump length so short hops between neighbouring gates don't balloon, capped
            // so a jump across the whole grid stays a gentle bow.
            const dx = b.x - a.x, dy = b.y - a.y;
            const len = Math.hypot(dx, dy) || 1;
            const bow = Math.min(len * 0.3, cellSize * 1.1);
            const cx = (a.x + b.x) / 2 - (dy / len) * bow;
            const cy = (a.y + b.y) / 2 + (dx / len) * bow;

            this.ctx.beginPath();
            this.ctx.moveTo(a.x, a.y);
            this.ctx.quadraticCurveTo(cx, cy, b.x, b.y);
            this.ctx.stroke();
        }
    }

    /** Draw teal hint at rim when path stops right after a rim-jump */
    private drawRimJumpHint(result: TraceResult): void {
        if (!result.rimJumpHint || result.won) return;

        const point = result.rimJumpHint;
        const pixel = this.getGridPointPixel(point);
        const { cellSize } = this.config;

        this.ctx.strokeStyle = this.lineConfig.lineColor;
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([4, 4]);

        this.ctx.beginPath();
        this.ctx.moveTo(pixel.x, pixel.y);

        // Draw inward from the rim
        if (point.row === 0) {
            this.ctx.lineTo(pixel.x, pixel.y + cellSize / 2);
        } else if (point.row === this.grid.size) {
            this.ctx.lineTo(pixel.x, pixel.y - cellSize / 2);
        } else if (point.col === 0) {
            this.ctx.lineTo(pixel.x + cellSize / 2, pixel.y);
        } else if (point.col === this.grid.size) {
            this.ctx.lineTo(pixel.x - cellSize / 2, pixel.y);
        }

        this.ctx.stroke();
        this.ctx.setLineDash([]);
    }

    /** Faint ghost of the start pin's half-disc at the "ghost start" — the rim point
     *  that would trace the longest in-order line — so a player who nudged the start
     *  off their drawn edge can find where it belongs. A single thick semicircle arc
     *  filling the same band as the pin's visible half-ring, at a low alpha. */
    private drawGhostStart(game: LineGame): void {
        const ghost = game.getGhostStartPoint();
        if (!ghost) return;

        const pixel = this.getGridPointPixel(ghost);
        const R = 12;
        const size = this.grid.size;

        // Rotate so the dome bulges outward past the rim, like the real pin.
        let angle = 0;
        if (ghost.row === 0) angle = 0;
        else if (ghost.row === size) angle = Math.PI;
        else if (ghost.col === 0) angle = -Math.PI / 2;
        else if (ghost.col === size) angle = Math.PI / 2;

        this.ctx.save();
        this.ctx.translate(pixel.x, pixel.y);
        this.ctx.rotate(angle);
        this.ctx.strokeStyle = colorWithAlpha('teal', 0.4);
        this.ctx.lineWidth = R * 0.5;    // fills the R·0.5 → R band (the pin's half-ring)
        this.ctx.lineCap = 'butt';       // flat ends along the diameter
        this.ctx.setLineDash([]);
        this.ctx.beginPath();
        this.ctx.arc(0, 0, R * 0.75, Math.PI, 2 * Math.PI);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /**
     * Moving the start is a placement, not a journey: the rim is a ring of
     * points, and a pin sliding round its corners to reach the far side would
     * claim a route the line never takes. So the old pin is pulled up as the new
     * one is planted — both at once, and the `exit` being shorter than the
     * `enter` keeps the overlap brief.
     */
    private syncPinTween(game: LineGame, now: number): void {
        const point = game.getStartPoint();
        const same = point && this.pinPoint &&
            point.row === this.pinPoint.row && point.col === this.pinPoint.col;
        if (same) return;

        if (this.pinPoint === null || !this.tweens.isEnabled()) {
            this.pinEnterStart = null;
            this.pinExit = null;
        } else {
            this.pinExit = { point: this.pinPoint, start: now };
            this.pinEnterStart = point ? now : null;
        }
        this.pinPoint = point ?? null;
    }

    private drawStartPoint(game: LineGame): void {
        const now = this.tweens.now();

        // The pin left behind, shrinking away at its old rim point.
        if (this.pinExit) {
            const { value, done } = this.tweens.tween(this.pinExit.start, MOTION.exit, now);
            if (done) this.pinExit = null;
            else {
                this.animating = true;
                this.drawPin(this.pinExit.point, 1 - value);
            }
        }

        const startPoint = game.getStartPoint();
        if (!startPoint) return;

        let scale = 1;
        if (this.pinEnterStart !== null) {
            const { value, done } = this.tweens.tween(this.pinEnterStart, MOTION.enter, now);
            if (done) this.pinEnterStart = null;
            else {
                this.animating = true;
                scale = value;
            }
        }
        this.drawPin(startPoint, scale);
    }

    /** The start pin at a rim point, at `scale` of its full size. */
    private drawPin(startPoint: Point, scale: number): void {
        if (scale <= 0.01) return;

        const pixel = this.getGridPointPixel(startPoint);
        const R = 12;
        const tip = R * 1.7;  // how far the point reaches into the grid
        const size = this.grid.size;

        // Rotate so local +y points inward (into the grid) for whichever rim edge.
        let angle = 0;
        if (startPoint.row === 0) angle = 0;
        else if (startPoint.row === size) angle = Math.PI;
        else if (startPoint.col === 0) angle = -Math.PI / 2;
        else if (startPoint.col === size) angle = Math.PI / 2;

        this.ctx.save();
        this.ctx.translate(pixel.x, pixel.y);
        this.ctx.rotate(angle);
        this.ctx.scale(scale, scale);

        // Pin shape: a half-disc bulging outward past the rim, joined to a
        // triangle whose point reaches into the grid.
        this.ctx.fillStyle = this.lineConfig.lineColor;
        this.ctx.beginPath();
        this.ctx.arc(0, 0, R, Math.PI, 2 * Math.PI);  // outward dome (y ≤ 0)
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.beginPath();
        this.ctx.moveTo(-R, 0);
        this.ctx.lineTo(R, 0);
        this.ctx.lineTo(0, tip);
        this.ctx.closePath();
        this.ctx.fill();

        // Accent hole punched out concentric with the half-disc.
        this.ctx.fillStyle = this.config.backgroundColor;
        this.ctx.beginPath();
        this.ctx.arc(0, 0, R * 0.5, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.restore();
    }

    private drawLastTargetDiamonds(game: LineGame, result: TraceResult): void {
        const size = this.lineConfig.diamondSize;
        const now = this.tweens.now();

        for (const { point, color } of this.getEndCorners(game, result)) {
            const pixel = this.getGridPointPixel(point);

            // A diamond is the corner that *completed* a digit, so it lands with the
            // same swing as the dots — the biggest of the game's small moments.
            const scale = this.hitPopScale(pointKey(point), now);
            this.ctx.save();
            this.ctx.translate(pixel.x, pixel.y);
            this.ctx.scale(scale, scale);

            this.ctx.fillStyle = color;
            this.drawDiamond(0, 0, size);

            // Four pips always close the sequence, whether the digit had four targets
            // or the two of a rim digit. They keep the dots' pip size, nudged a little
            // further out than a dot's would be since they aim at the diamond's points.
            this.drawPips(0, 0, this.lineConfig.targetRadius, 4,
                this.config.backgroundColor, size * 0.38);
            this.ctx.restore();
        }
    }

    private drawDiamond(x: number, y: number, size: number): void {
        this.ctx.beginPath();
        this.ctx.moveTo(x, y - size);
        this.ctx.lineTo(x + size, y);
        this.ctx.lineTo(x, y + size);
        this.ctx.lineTo(x - size, y);
        this.ctx.closePath();
        this.ctx.fill();
    }

}
